import React from 'react';
import { ShieldCheck, Lock, Database, UserCheck, Share2, Mail } from 'lucide-react';

const sections = [
  {
    id: "collection",
    icon: <Database className="w-5 h-5 text-blue-600" />,
    title: "البيانات التي نجمعها",
    color: "bg-blue-50",
    body: [
      "نجمع البيانات التي تقدمها لنا مباشرة عند حجز موعد أو التواصل معنا، مثل الاسم ورقم الهاتف والبريد الإلكتروني.",
      "نجمع البيانات الطبية اللازمة لإجراء التحاليل وتسجيل نتائجها، وتُحفظ هذه البيانات بسرية تامة ولا تُستخدم إلا لغرض تقديم الخدمة.",
      "قد نجمع بيانات تقنية محدودة عند استخدامك لموقعنا الإلكتروني، مثل نوع المتصفح ووقت الزيارة، لتحسين تجربة الاستخدام."
    ]
  },
  {
    id: "usage",
    icon: <UserCheck className="w-5 h-5 text-teal-600" />,
    title: "كيف نستخدم بياناتك",
    color: "bg-teal-50",
    body: [
      "لتنفيذ الفحوصات والتحاليل المطلوبة وتسليم النتائج بدقة وفي الوقت المناسب.",
      "للتواصل معك بخصوص المواعيد أو نتائج التحاليل أو أي استفسار تقدمت به.",
      "لتحسين خدماتنا وتطوير تجربة العملاء بناءً على الملاحظات والإحصاءات العامة."
    ]
  },
  {
    id: "protection",
    icon: <Lock className="w-5 h-5 text-purple-600" />,
    title: "حماية البيانات",
    color: "bg-purple-50",
    body: [
      "نتخذ إجراءات تقنية وإدارية مناسبة لحماية بياناتك من الوصول غير المصرح به أو الفقد أو سوء الاستخدام.",
      "يقتصر الوصول إلى بياناتك الطبية على الفريق الطبي والإداري المخوّل فقط، والملتزم بمعايير السرية المهنية.",
      "نحتفظ ببياناتك للمدة اللازمة فقط لتحقيق الغرض الذي جُمعت من أجله، أو وفقًا لما تقتضيه الأنظمة المعمول بها."
    ]
  },
  {
    id: "sharing",
    icon: <Share2 className="w-5 h-5 text-amber-600" />,
    title: "مشاركة البيانات",
    color: "bg-amber-50",
    body: [
      "لا نقوم ببيع أو تأجير بياناتك الشخصية أو الطبية لأي طرف ثالث لأغراض تسويقية.",
      "قد تتم مشاركة بيانات محدودة مع الجهات الطبية المعالجة بناءً على طلبك أو موافقتك الصريحة.",
      "قد نُفصح عن بيانات إذا طُلب ذلك بموجب القانون أو أمر من جهة رسمية مختصة."
    ]
  }
];

const PrivacyPolicy = () => {
  return (
    <section id="privacy-policy" className="py-24 bg-slate-50 font-sans" dir="rtl">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 text-blue-700 text-sm font-semibold px-4 py-1.5 mb-5">
            <ShieldCheck className="w-4 h-4" />
            خصوصيتك أولويتنا
          </span>
          <h2 className="text-3xl md:text-5xl font-extrabold text-blue-900 mb-5 tracking-tight">
            سياسة الخصوصية
          </h2>
          <div className="w-16 h-1 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 mx-auto mb-6"></div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            نلتزم في معمل النخبة بحماية بياناتك الشخصية والطبية، وتوضح هذه السياسة كيفية جمع بياناتك واستخدامها وحمايتها
          </p>
          <p className="text-sm text-gray-400 mt-4">آخر تحديث: يوليو 2026</p>
        </div>

        <div className="space-y-6">
          {sections.map((section) => (
            <div
              key={section.id}
              id={section.id}
              className={`${section.color} p-6 rounded-2xl shadow-sm border border-gray-100`}
            >
              <div className="flex items-start gap-4">
                <div className="p-3 bg-white rounded-xl shadow-sm shrink-0">
                  {section.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    {section.title}
                  </h3>
                  <ul className="space-y-2">
                    {section.body.map((line, i) => (
                      <li key={i} className="text-gray-600 leading-relaxed flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 mt-2 shrink-0"></span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 bg-white shadow-lg rounded-2xl p-8 border border-gray-100 text-center">
          <div className="p-3 bg-teal-50 rounded-xl shadow-sm inline-flex mb-4">
            <Mail className="w-6 h-6 text-teal-600" />
          </div>
          <h3 className="text-xl font-bold text-blue-900 mb-2">لديك سؤال عن خصوصيتك؟</h3>
          <p className="text-gray-600 mb-1">
            إذا كان لديك أي استفسار بخصوص سياسة الخصوصية أو ترغب في تعديل بياناتك أو حذفها، يمكنك التواصل معنا على
          </p>
          <p className="text-blue-700 font-semibold">support@medicallab.com</p>
        </div>
      </div>
    </section>
  );
};

export default PrivacyPolicy;
